import { API } from '../constants/constants';
import { normalizeServiceId } from '../utils/serviceLogNormalization';
import { normalizeToSubs } from '../../utils/eventHandlerUtils';
import { normalizeId, normalizeLeadIds } from '../utils/mediaInteractionNormalization';

const normalizeIdArray = (value) => {
  if (value === null) {
    return [];
  }
  let list = [];
  if (Array.isArray(value)) {
    list = value;
  } else if (value) {
    list = [value];
  }
  return list.map((item) => normalizeId(item)).filter(Boolean);
};

const normalizeTypes = (value) => {
  if (value === null) {
    return [];
  }
  let list = [];
  if (Array.isArray(value)) {
    list = value;
  } else if (value) {
    list = [value];
  }
  return list
    .map((item) => {
      if (!item || typeof item === 'string') {
        return null;
      }
      const typeId = normalizeId(item);
      if (!typeId || !item.name || !item.type) {
        return null;
      }
      return {
        _id: typeId,
        name: item.name,
        type: item.type,
        backgroundColor: item.backgroundColor,
        permissions: item.permissions,
        proactivity: item.proactivity,
        archived: item.archived,
      };
    })
    .filter(Boolean);
};

const normalizeContentType = (value) => {
  if (!value || Array.isArray(value) || typeof value === 'string') {
    return null;
  }
  const typeId = normalizeId(value);
  if (!typeId || !value.name) {
    return null;
  }
  return {
    _id: typeId,
    name: value.name,
    contentType: value.contentType ?? 'Content',
    backgroundColor: value.backgroundColor,
    archived: value.archived,
  };
};

const normalizeCampaigns = (value) => {
  if (value === null) {
    return [];
  }
  let list = [];
  if (Array.isArray(value)) {
    list = value;
  } else if (value) {
    list = [value];
  }
  return list
    .map((item) => {
      if (!item || typeof item === 'string') {
        return null;
      }
      const campaignId = normalizeId(item);
      if (!campaignId || !item.name) {
        return null;
      }
      return {
        _id: campaignId,
        name: item.name,
        backgroundColor: item.backgroundColor,
        archived: item.archived,
      };
    })
    .filter(Boolean);
};

const normalizeLinkedServices = (value) => {
  if (value === null) {
    return null;
  }
  let list = [];
  if (Array.isArray(value)) {
    list = value;
  } else if (value) {
    list = [value];
  }
  return list
    .map((item) => {
      if (!item) {
        return null;
      }
      if (typeof item === 'string') {
        return { _id: item, name: '' };
      }
      const serviceId = normalizeId(item);
      if (!serviceId) {
        return null;
      }
      const name = item.name ?? item.service ?? item.title ?? '';
      return { _id: serviceId, name };
    })
    .filter(Boolean);
};

const normalizeBulkInteractionFields = (fields) => {
  if (!fields || typeof fields !== 'object') {
    return fields;
  }
  const normalized = { ...fields };

  if (Object.hasOwn(fields, 'lead')) {
    normalized.lead = normalizeLeadIds(fields.lead);
  }
  if (Object.hasOwn(fields, 'type')) {
    normalized.type = normalizeTypes(fields.type);
  }
  if (Object.hasOwn(fields, 'campaign')) {
    normalized.campaign = normalizeCampaigns(fields.campaign);
  }
  if (Object.hasOwn(fields, 'expert')) {
    normalized.expert = normalizeIdArray(fields.expert);
  }
  if (Object.hasOwn(fields, 'journalist')) {
    normalized.journalist = normalizeId(fields.journalist) ?? null;
  }
  if (Object.hasOwn(fields, 'outlet')) {
    normalized.outlet = normalizeId(fields.outlet) ?? null;
  }
  if (Object.hasOwn(fields, 'linkedService')) {
    normalized.linkedService = normalizeLinkedServices(fields.linkedService);
  }

  return normalized;
};

const normalizeEngagementBulkFields = (fields) => {
  if (!fields || !fields.lead) {
    return fields;
  }

  return {
    ...fields,
    lead: normalizeToSubs(fields.lead),
  };
};

const normalizeToArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value) {
    return [value];
  }

  return [];
};

const contentLogBulkFieldNormalizers = {
  topic: (value) => ['topic', value],
  entryDate: (value) => ['entryDate', value],
  dueDate: (value) => ['dueDate', value],
  contentType: (value) => ['contentType', normalizeContentType(value)],
  type: (value) => ['types', normalizeTypes(value)],
  units: (value) => ['units', normalizeToArray(value)],
  expert: (value) => ['experts', normalizeIdArray(value)],
  dept: (value) => ['depts', normalizeToArray(value)],
  campaign: (value) => ['campaigns', normalizeCampaigns(value)],
  audience: (value) => ['audiences', normalizeIdArray(value)],
  lead: (value) => ['leads', normalizeToSubs(value)],
  status: (value) => ['status', value],
  otherNotes: (value) => ['otherNotes', value],
  content: (value) => ['content', value],
  background: (value) => ['background', value],
};

const normalizeContentLogBulkFields = (fields) => {
  if (!fields || typeof fields !== 'object') {
    return fields;
  }

  const normalized = {};

  Object.entries(contentLogBulkFieldNormalizers).forEach(([fieldName, normalizeField]) => {
    if (!Object.hasOwn(fields, fieldName)) {
      return;
    }

    const [normalizedKey, normalizedValue] = normalizeField(fields[fieldName]);
    normalized[normalizedKey] = normalizedValue;
  });

  return normalized;
};

export const bulkUpdateServices = async (ids, fields) => {
  try {
    const normalizedIds = (ids || []).map((id) => normalizeServiceId(id)).filter(Boolean);
    if (normalizedIds.length === 0) {
      throw new Error('invalid service ids');
    }
    const res = await API.put(`/services/bulkUpdate`, {
      ids: normalizedIds,
      fields,
    });
    return res.data;
  } catch (e) {
    throw new Error(e.message);
  }
};

export const bulkUpdateInteractions = async (ids, fields) => {
  try {
    const res = await API.put(`/issues/bulkUpdate`, {
      ids,
      fields: normalizeBulkInteractionFields(fields),
    });
    return res.data;
  } catch (e) {
    throw new Error(e.message);
  }
};

export const bulkUpdateEngagements = async (ids, fields) => {
  try {
    const normalizedFields = normalizeEngagementBulkFields(fields);
    const res = await API.put(`/engagements/bulkUpdate`, {
      ids,
      fields: normalizedFields,
    });
    return res.data;
  } catch (e) {
    throw new Error(e.message);
  }
};

export const bulkUpdateContentLogs = async (ids, fields) => {
  try {
    const res = await API.put(`/content-logs/bulkUpdate`, {
      ids,
      fields: normalizeContentLogBulkFields(fields),
    });
    return res.data;
  } catch (e) {
    throw new Error(e.message);
  }
};
